import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  updateIsFetchingRoutineSuggestions,
  updateIsRefiningRoutineHabits
} from './slice';
import focusBearApi from 'services/axios-config';
import { isAbortLike } from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import { ROUTINE_SUGGESTIONS_ASYNC_ERROR_I18N_KEY } from 'constants/routine-suggestions-revamp';
import { captureException } from 'services/posthog.service';
import { ActivityType } from 'interfaces/common.interface';
import { t } from 'services/i18n.service';
import {
  extractAsyncTaskId,
  pollRoutineSuggestionsResult,
  RoutineSuggestionsAsyncError
} from 'utils/routine-suggestions-async.util';
import { getApiLanguageCode } from 'utils/helpers';

export const getRoutineSuggestionActivities = createAsyncThunk(
  'routine-suggestions/get_routine_suggestion_activities',
  async (
    payload: {
      user_goals:
        | string[]
        | {
            goal: string;
            isCustom: boolean;
          }[];
      lang: string;
      routine_duration: number;
      routine?: string;
    },
    { dispatch, signal }
  ) => {
    try {
      dispatch(updateIsFetchingRoutineSuggestions(true));
      const { data } = await focusBearApi.post(
        Endpoints.ACTIVITY_LIBRARY_ROUTINE_SUGGESTIONS_ASYNC,
        {
          ...payload,
          lang: getApiLanguageCode(payload.lang),
          groupByGoals: true
        },
        { signal }
      );

      const asyncTaskId = extractAsyncTaskId(data);
      if (asyncTaskId) {
        const result = await pollRoutineSuggestionsResult(asyncTaskId, signal);
        return result;
      }

      return (data ?? {}) as Record<string, ActivityType[]>;
    } catch (error) {
      dispatch(updateIsFetchingRoutineSuggestions(false));
      if (isAbortLike(error)) {
        throw error;
      }
      if (error instanceof RoutineSuggestionsAsyncError) {
        toast.error(t(ROUTINE_SUGGESTIONS_ASYNC_ERROR_I18N_KEY[error.code]));
      }
      captureException(error);
    }
  }
);

export const getRefinedRoutineHabitsWithAI = createAsyncThunk(
  'routine-suggestions/get_refined_routine_habits_with_ai',
  async (
    payload: {
      current_habits: ActivityType[];
      user_goals: string[];
      routine_duration: number;
      user_feedback: string;
      routine: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsRefiningRoutineHabits(true));
      const { routine, ...restPayload } = payload;
      const { data } = await focusBearApi.post(
        Endpoints.ACTIVITY_LIBRARY_ADJUST_HABITS_WITH_AI,
        {
          ...restPayload,
          groupByGoals: true
        }
      );
      return { data: data || {}, routine };
    } catch (error) {
      dispatch(updateIsRefiningRoutineHabits(false));
      captureException(error);
    }
  }
);
